import { Component, forwardRef, HostBinding, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { AbstractControl, NG_VALIDATORS, ValidationErrors, Validator } from '@angular/forms';
import {
  NgbDateParserFormatter,
  NgbDatepickerConfig,
  NgbDateStruct,
  NgbInputDatepicker
} from '@ng-bootstrap/ng-bootstrap';

import { valueAccessorFactory } from '../../helpers';
import { OutsideClickListenerService } from '../../providers';
import { hasValue } from '../../util';
import { BaseInputDirective } from '../base-input.directive';
import { formControlFactory } from '../form-control.provider';
import { InputSize } from '../input';
import { dateInRangeValidator } from './date-in-range-validator';
import { PcsNgbDateParserFormatter } from './date-parser-formatter';
import { PcsDate } from './pcs-date';
import { toNgbDate } from './utils';

enum OutsideDays {
  VISIBLE = 'visible',
  COLLAPSED = 'collapsed',
  HIDDEN = 'hidden'
}

@Component({
  selector: 'pcs-datepicker',
  templateUrl: './datepicker.component.html',
  styleUrls: ['./datepicker.component.scss'],
  providers: [
    NgbDatepickerConfig,
    valueAccessorFactory(DatepickerComponent),
    formControlFactory(DatepickerComponent),
    {
      provide: NgbDateParserFormatter,
      useClass: PcsNgbDateParserFormatter
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => DatepickerComponent),
      multi: true
    },
    OutsideClickListenerService,
  ]
})
export class DatepickerComponent extends BaseInputDirective implements OnChanges, OnInit, Validator {

  @Input() label: string;
  @Input() size = InputSize.Small;
  @Input() minDate: PcsDate = null;
  @Input() maxDate: PcsDate = null;
  @Input() @HostBinding('class.row') horizontal = false;

  date: NgbDateStruct;
  modelValue: any;
  labelClasses = null;
  inputWrapperClasses = null;

  @ViewChild(NgbInputDatepicker)
  private datePicker: NgbInputDatepicker;

  private onValidatorChange: () => void;

  @HostBinding('class.mb-half')
  private get editAndSmall() {
    return !this.readonly && this.isSmall();
  }

  @HostBinding('class.mb-0')
  private get readOnlyAndSmall() {
    return this.readonly && this.isSmall();
  }

  constructor(
    public ngbDatepickerConfig: NgbDatepickerConfig,
    public outsideClickListener: OutsideClickListenerService
  ) {
    super();
  }

  ngOnInit(): void {
    this.labelClasses = this.calculateLabelClasses();
    this.inputWrapperClasses = this.calculateInputWrapperClasses();
  }

  ngOnChanges({minDate, maxDate, horizontal, size}: SimpleChanges): void {
    if (minDate || maxDate) {
      this.checkForBoundaries();
      this.onValidatorChange?.();
    }

    if (horizontal || size) {
      this.labelClasses = this.calculateLabelClasses();
      this.inputWrapperClasses = this.calculateInputWrapperClasses();
    }
  }

  writeValue(obj: PcsDate): void {
    this.modelValue = obj;
    if (obj) {
      this.date = toNgbDate(this.modelValue);
    } else {
      this.date = null;
    }
  }

  onChange(event): void {
    this.modelValue = hasValue(event) ? this.toLocalDate(event) : null;
    this.propagateChange(this.modelValue);
  }

  onBlur(event): void {
    this.modelValue = this.toLocalDate(event);
    this.propagateTouched(this.modelValue);
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    return this.shouldValidate() && dateInRangeValidator(this.minDate, this.maxDate)(control);
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  closeDatePicker() {
    this.datePicker.close();
    this.outsideClickListener.unbindDocumentClickListener();
  }

  toggle() {
    this.datePicker.toggle();
    if (this.datePicker.isOpen()) {
      this.outsideClickListener.bindDocumentClickListener(this.closeDatePicker.bind(this));
    } else {
      this.outsideClickListener.unbindDocumentClickListener();
    }
  }

  private checkForBoundaries(): void {
    if (this.minDate || this.maxDate) {
      this.ngbDatepickerConfig.maxDate = toNgbDate(this.maxDate);
      this.ngbDatepickerConfig.minDate = toNgbDate(this.minDate);
      this.ngbDatepickerConfig.outsideDays = OutsideDays.HIDDEN;
    }
  }

  private shouldValidate() {
    return this.minDate || this.maxDate;
  }

  private toLocalDate({year = null, month = null, day = null}): PcsDate {
    return [
      year,
      month,
      day
    ];
  }

  private calculateLabelClasses() {
    return {
      'col-sm-2 col-form-label': this.horizontal,
      'col-form-label-sm': this.isSmall(),
      'col-form-label-lg': this.isLarge()
    };
  }

  private calculateInputWrapperClasses() {
    return {
      'col-sm-10': this.horizontal,
      'input-group-sm': this.isSmall(),
      'input-group-lg': this.isLarge()
    };
  }

  private isSmall() {
    return this.size === InputSize.Small;
  }

  private isLarge() {
    return this.size === InputSize.Large;
  }
}
